import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  confirm,
  Input,
  Modal,
  Textarea,
} from "@devhop/ui";
import { Check, PlusIcon, Settings, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { WorkItemTypeDTO } from "@/modules/qc/domain/contracts/work-item-type";
import type { QCStandardByIdResult } from "@/modules/qc/domain/types";
import type { QCRecordByIdResult } from "@/modules/qc/domain/types/record";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import { formatDateTimeForInput, toISOForAPI } from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { AppImage } from "@/shared/ui/AppImage";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SmartFileUpload } from "@/shared/ui/SmartFileUpload";
import {
  useCreateWorkItemType,
  useDeleteWorkItemType,
  useUpdateWorkItemType,
  useWorkItemTypes,
} from "../api/queries";
import { WorkItemTypeForm } from "./WorkItemTypeForm";

type WorkItemTypeItem = { id: string; name: string };

type QCRecordItemState = {
  qcStandardItemId: string;
  status: "pass" | "fail" | null;
  notes: string;
  photoKey: string | null; // S3 key for photo (e.g. uploads/qc/xxx.jpg)
};

type QCRecordFormProps = {
  qcStandard: QCStandardByIdResult;
  existingRecord?: QCRecordByIdResult;
  isEdit: boolean;
  onSubmit: (data: {
    inspectedAt: string;
    workItemId: string;
    workItemTypeId: string;
    notes: string;
    location: string;
    items: Array<{
      qcStandardItemId: string;
      status: "pass" | "fail";
      notes: string | null;
      photoKey: string | null;
    }>;
    attachments: string[]; // Array of S3 fileKeys
  }) => Promise<void>;
  submitting: boolean;
};

export function QCRecordForm({
  qcStandard,
  existingRecord,
  isEdit,
  onSubmit,
  submitting,
}: QCRecordFormProps) {
  // Initialize form state
  const [items, setItems] = useState<QCRecordItemState[]>([]);
  const [inspectedAt, setInspectedAt] = useState(
    formatDateTimeForInput(new Date()),
  );
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState("");
  const [workItemId, setWorkItemId] = useState("");
  const [workItemTypeId, setWorkItemTypeId] = useState("");

  // Validation error state
  const [validationError, setValidationError] = useState<string | null>(null);

  // Initialize items from QC standard or existing record
  useEffect(() => {
    if (isEdit && existingRecord?.items) {
      // Edit mode: load from existing record
      // Note: existingRecord.items has photoUrl but not mediaAssetId
      // We'll use photoUrl for preview, but need to find mediaAssetId from attachments
      // For now, we'll use photoUrl as preview and set mediaAssetId to null
      // The user can re-select the media if needed
      setItems(
        existingRecord.items.map((item) => ({
          qcStandardItemId: item.qcStandardItemId,
          status: item.status,
          notes: item.notes || "",
          photoKey: item.photoUrl || null, // photoUrl stores key or path
        })),
      );
      setInspectedAt(formatDateTimeForInput(existingRecord.inspectedAt));
      setNotes(existingRecord.notes || "");
      setLocation(existingRecord.location || "");
      setWorkItemId(existingRecord.workItemId || "");
      setWorkItemTypeId(existingRecord.workItemTypeId || "");
    } else if (qcStandard?.items) {
      // Create mode: initialize from QC standard
      setItems(
        qcStandard.items
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map((item) => ({
            qcStandardItemId: item.id,
            status: null as "pass" | "fail" | null,
            notes: "",
            photoKey: null,
          })),
      );
    }
  }, [qcStandard, existingRecord, isEdit]);

  // Calculate overall status
  const overallStatus = useMemo(() => {
    if (items.length === 0) return "pending";
    const allChecked = items.every((item) => item.status !== null);
    if (!allChecked) return "pending";
    const hasFail = items.some((item) => item.status === "fail");
    return hasFail ? "fail" : "pass";
  }, [items]);

  // Check if all required items are checked
  const allItemsChecked = useMemo(() => {
    if (!qcStandard?.items) return false;
    const requiredItems = qcStandard.items.filter((item) => item.isRequired);
    return requiredItems.every((reqItem) =>
      items.some(
        (item) => item.qcStandardItemId === reqItem.id && item.status !== null,
      ),
    );
  }, [items, qcStandard]);

  const handleItemStatusChange = (
    qcStandardItemId: string,
    status: "pass" | "fail",
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.qcStandardItemId === qcStandardItemId ? { ...item, status } : item,
      ),
    );
  };

  const handleItemNotesChange = (qcStandardItemId: string, notes: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.qcStandardItemId === qcStandardItemId ? { ...item, notes } : item,
      ),
    );
  };

  const handleItemPhotoChange = (
    qcStandardItemId: string,
    key: string | null,
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.qcStandardItemId === qcStandardItemId
          ? { ...item, photoKey: key }
          : item,
      ),
    );
  };

  const handleSubmit = async () => {
    // Clear previous validation errors
    setValidationError(null);

    if (!allItemsChecked) {
      setValidationError("ກະລຸນາກວດສອບທຸກລາຍການທີ່ຈໍາເປັນ");
      return;
    }

    // Get final items with photoKeys
    const recordItems = items
      .filter((item) => item.status !== null)
      .map((item) => {
        const status = item.status;
        if (!status) return null;

        return {
          qcStandardItemId: item.qcStandardItemId,
          status,
          notes: item.notes || null,
          photoKey: item.photoKey || null,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    // Collect fileKeys from items for attachments array
    const attachmentFileKeys = recordItems
      .map((item) => item.photoKey)
      .filter((key): key is string => !!key);

    // Validate requirePhoto
    if (qcStandard?.requirePhoto) {
      const itemsWithPhoto = recordItems.filter((item) => item.photoKey);
      const hasAttachments = attachmentFileKeys.length > 0;

      if (itemsWithPhoto.length === 0 && !hasAttachments) {
        setValidationError("ຕ້ອງເລືອກຮູບພາບຢ່າງໜ້ອຍ 1 ຮູບ");
        return;
      }
    }

    // Validate requireNotesOnFail
    if (qcStandard?.requireNotesOnFail) {
      const failItemsWithoutNotes = recordItems.filter(
        (item) =>
          item.status === "fail" && (!item.notes || item.notes.trim() === ""),
      );

      if (failItemsWithoutNotes.length > 0) {
        setValidationError("ລາຍການທີ່ບໍ່ຜ່ານຕ້ອງມີຫມາຍເຫດ");
        return;
      }
    }

    const inspectedAtISO = toISOForAPI(inspectedAt);
    if (!inspectedAtISO) {
      setValidationError("ກະລຸນາກວດສອບວັນເວລາກວດສອບ");
      return;
    }

    await onSubmit({
      inspectedAt: inspectedAtISO,
      workItemId: workItemId || "",
      workItemTypeId: workItemTypeId || "",
      notes: notes || "",
      location: location || "",
      items: recordItems,
      attachments: attachmentFileKeys,
    });
  };

  const sortedItems = items
    .map((item) => {
      const standardItem = qcStandard?.items?.find(
        (si) => si.id === item.qcStandardItemId,
      );
      return {
        ...item,
        standardItem,
        orderIndex: standardItem?.orderIndex ?? 0,
      };
    })
    .sort((a, b) => a.orderIndex - b.orderIndex);

  // Work Item Type Management Component
  function WorkItemTypeManagementButton() {
    const canManage = useActionPermission([
      "qc:create",
      "qc:update",
      "qc:delete",
    ]);
    const listModal = useDisclosure();
    const createModal = useDisclosure();
    const editModal = useDisclosure<WorkItemTypeDTO>();
    const listQuery = useWorkItemTypes({ limit: 100, offset: 0 });
    const createWorkItemType = useCreateWorkItemType();
    const updateWorkItemType = useUpdateWorkItemType();
    const deleteWorkItemType = useDeleteWorkItemType();

    if (!canManage) return null;

    return (
      <>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => listModal.open()}
          title="ຈັດການປະເພດງານທີ່ກວດສອບ"
        >
          <Settings className="h-4 w-4" />
        </Button>

        {/* Work Item Types List Modal */}
        <Modal
          open={!!listModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              listModal.close();
              createModal.close();
              editModal.close();
            }
          }}
          title="ຈັດການປະເພດງານທີ່ກວດສອບ"
          description="ສ້າງ, ແກ້ໄຂ, ຫຼື ລຶບປະເພດງານທີ່ກວດສອບ"
          size="md"
        >
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  listModal.close();
                  createModal.open();
                }}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                ສ້າງປະເພດງານໃໝ່
              </Button>
            </div>

            {listQuery.isLoading ? (
              <div className="py-8 text-center text-muted-foreground">
                ກຳລັງໂຫຼດ...
              </div>
            ) : listQuery.data?.data.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                ຍັງບໍ່ມີປະເພດງານ
              </div>
            ) : (
              <div className="space-y-2">
                {listQuery.data?.data.map((workItemType) => (
                  <div
                    key={workItemType.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{workItemType.name}</span>
                      {workItemType.description && (
                        <span className="text-muted-foreground text-xs">
                          {workItemType.description}
                        </span>
                      )}
                      <span className="text-muted-foreground text-xs">
                        ລໍາດັບ: {workItemType.orderIndex}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          listModal.close();
                          editModal.openWith(workItemType);
                        }}
                      >
                        ແກ້ໄຂ
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບປະເພດງານ "${workItemType.name}"?`,
                              description: "ປະເພດງານນີ້ຈະລຶບອອກຈາກລາຍການທັງຫມົດ",
                              actionText: "ລຶບ",
                              ActionProps: {
                                variant: "destructive",
                              },
                            })
                          ) {
                            await deleteWorkItemType.mutateAsync(
                              workItemType.id,
                            );
                          }
                        }}
                      >
                        ລຶບ
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>

        {/* Create Work Item Type Modal */}
        <Modal
          open={!!createModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              createModal.close();
            } else {
              listModal.close();
            }
          }}
          title="ສ້າງປະເພດງານທີ່ກວດສອບ"
          description="ກໍານົດຊື່, ຄໍາອະທິບາຍ, ແລະລໍາດັບຂອງປະເພດງານທີ່ກວດສອບ."
          size="sm"
        >
          <WorkItemTypeForm
            onSubmit={async (vals) => {
              await createWorkItemType.mutateAsync({
                name: vals.name || "",
                description: vals.description ?? null,
                orderIndex: vals.orderIndex ?? 0,
              });
              createModal.close();
              listModal.open();
            }}
            submitting={createWorkItemType.isPending}
          />
        </Modal>

        {/* Edit Work Item Type Modal */}
        <Modal
          open={!!editModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              editModal.close();
            } else {
              listModal.close();
            }
          }}
          title="ແກ້ໄຂປະເພດງານທີ່ກວດສອບ"
          description="ປັບປຸງລາຍລະອຽດຂອງປະເພດງານທີ່ກວດສອບ."
          size="sm"
        >
          <WorkItemTypeForm
            initialValues={
              editModal.data
                ? {
                    name: editModal.data.name,
                    description: editModal.data.description,
                    orderIndex: editModal.data.orderIndex,
                  }
                : undefined
            }
            onSubmit={async (vals) => {
              if (!editModal.data) return;
              await updateWorkItemType.mutateAsync({
                id: editModal.data.id,
                input: {
                  name: vals.name || "",
                  description: vals.description ?? null,
                  orderIndex: vals.orderIndex ?? 0,
                },
              });
              editModal.close();
              listModal.open();
            }}
            submitting={updateWorkItemType.isPending}
          />
        </Modal>
      </>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Inspection Info */}
      <div
        className="space-y-3 rounded-xl border bg-card p-4"
        data-tourid="record-form-inspection-info"
      >
        <h2 className="font-semibold text-lg">ຂໍ້ມູນການກວດສອບ</h2>
        <div className="space-y-3">
          <div>
            <label
              htmlFor="inspectedAt"
              className="mb-1 block font-medium text-sm"
            >
              ວັນທີກວດສອບ
            </label>
            <input
              id="inspectedAt"
              type="datetime-local"
              value={inspectedAt}
              onChange={(e) => setInspectedAt(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="location"
              className="mb-1 block font-medium text-sm"
            >
              ສະຖານທີ່ (ທາງເລືອກ)
            </label>
            <Input
              id="location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="ສະຖານທີ່ກວດສອບ"
            />
          </div>
          {/* Work Item Type */}
          <div>
            <label
              htmlFor="workItemTypeId"
              className="mb-1 block font-medium text-sm"
            >
              ປະເພດງານທີ່ກວດສອບ (ທາງເລືອກ)
            </label>
            <div className="flex items-center gap-2">
              <InfiniteCombobox<WorkItemTypeItem>
                queryKey={["qc-work-item-types", "record-form"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/qc/work-item-types/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem(
                    `${config.apiUrl}/qc/work-item-types/lookup`,
                    id,
                  )
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                value={workItemTypeId}
                onValueChange={setWorkItemTypeId}
                placeholder="ເລືອກປະເພດງານ"
                clearable
                className="flex-1"
              />
              <WorkItemTypeManagementButton />
            </div>
          </div>
          {/* Work Item ID */}
          <div>
            <label
              htmlFor="workItemId"
              className="mb-1 block font-medium text-sm"
            >
              ລະຫັດງານ (ທາງເລືອກ)
            </label>
            <Input
              id="workItemId"
              type="text"
              value={workItemId}
              onChange={(e) => setWorkItemId(e.target.value)}
              placeholder="ລະຫັດງານທີ່ກວດສອບ"
            />
          </div>
        </div>
      </div>

      {/* QC Items */}
      <div className="space-y-3" data-tourid="record-form-items">
        <h2 className="font-semibold text-lg">ລາຍການກວດສອບ</h2>
        {sortedItems.map((item, index) => {
          const standardItem = item.standardItem;
          if (!standardItem) return null;

          const isRequired = standardItem.isRequired;
          const isChecked = item.status !== null;

          return (
            <div
              key={item.qcStandardItemId}
              className={`rounded-xl border p-4 ${
                isChecked
                  ? item.status === "pass"
                    ? "border-green-500 bg-green-50 dark:bg-green-950"
                    : "border-red-500 bg-red-50 dark:bg-red-950"
                  : "bg-card"
              }`}
            >
              <div className="mb-3">
                <div className="mb-1 flex items-start justify-between">
                  <h3 className="font-semibold text-base">
                    {index + 1}. {standardItem.title}
                    {isRequired && (
                      <span className="ml-2 text-destructive">*</span>
                    )}
                  </h3>
                  {isChecked && (
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full ${
                        item.status === "pass" ? "bg-green-500" : "bg-red-500"
                      }`}
                    >
                      {item.status === "pass" ? (
                        <Check className="h-4 w-4 text-white" />
                      ) : (
                        <X className="h-4 w-4 text-white" />
                      )}
                    </div>
                  )}
                </div>
                {standardItem.description && (
                  <p className="text-muted-foreground text-sm">
                    {standardItem.description}
                  </p>
                )}
              </div>

              {/* Status Buttons - Large and mobile-friendly */}
              <div className="mb-3 flex gap-2">
                <Button
                  type="button"
                  variant={item.status === "pass" ? "default" : "outline"}
                  size="lg"
                  className="flex-1 py-6 text-base"
                  onClick={() =>
                    handleItemStatusChange(item.qcStandardItemId, "pass")
                  }
                >
                  <Check className="mr-2 h-5 w-5" />
                  ຜ່ານ
                </Button>
                <Button
                  type="button"
                  variant={item.status === "fail" ? "destructive" : "outline"}
                  size="lg"
                  className="flex-1 py-6 text-base"
                  onClick={() =>
                    handleItemStatusChange(item.qcStandardItemId, "fail")
                  }
                >
                  <X className="mr-2 h-5 w-5" />
                  ບໍ່ຜ່ານ
                </Button>
              </div>

              {/* Image Upload */}
              {isChecked && (
                <div className="mt-3 space-y-2">
                  <div className="mb-1 block font-medium text-sm">
                    ຮູບພາບ
                    {qcStandard?.requirePhoto && (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </div>
                  {item.photoKey ? (
                    <div className="flex items-start gap-2">
                      <AppImage
                        src={item.photoKey}
                        alt="ຮູບພາບ QC"
                        className="h-24 w-24 shrink-0 rounded-md border object-cover"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleItemPhotoChange(item.qcStandardItemId, null)
                        }
                        className="shrink-0"
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        ລຶບ
                      </Button>
                    </div>
                  ) : (
                    <SmartFileUpload
                      keyPrefix="uploads/qc"
                      accept="image/*"
                      onSuccess={(key) =>
                        handleItemPhotoChange(item.qcStandardItemId, key)
                      }
                      buttonLabel="ອັບໂຫຼດຮູບ"
                      hint={
                        qcStandard?.requirePhoto
                          ? "ກະລຸນາເລືອກຮູບພາບ (ໄຟລ໌ໃຫຍ່ກວ່າ 5MB ຈະອັບໂຫຼດແບບ chunk)"
                          : "ເລືອກຮູບພາບ (ທາງເລືອກ)"
                      }
                    />
                  )}
                </div>
              )}

              {/* Notes */}
              {isChecked && (
                <div className="mt-3">
                  <label
                    htmlFor={`notes-${item.qcStandardItemId}`}
                    className="mb-1 block font-medium text-sm"
                  >
                    ຫມາຍເຫດ
                    {item.status === "fail" &&
                      qcStandard?.requireNotesOnFail && (
                        <span className="ml-1 text-destructive">*</span>
                      )}
                  </label>
                  <Textarea
                    id={`notes-${item.qcStandardItemId}`}
                    value={item.notes}
                    onChange={(e) =>
                      handleItemNotesChange(
                        item.qcStandardItemId,
                        e.target.value,
                      )
                    }
                    placeholder="ເພີ່ມຫມາຍເຫດ..."
                    rows={2}
                    required={
                      item.status === "fail" && qcStandard?.requireNotesOnFail
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Validation Error Alert */}
      {validationError && (
        <Alert variant="destructive">
          <AlertTitle>ຂໍ້ຜິດພາດ</AlertTitle>
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      {/* Overall Notes */}
      <div
        className="rounded-xl border bg-card p-4"
        data-tourid="record-form-overall-notes"
      >
        <h2 className="mb-3 font-semibold text-lg">ຫມາຍເຫດທົ່ວໄປ</h2>
        <Textarea
          id="overall-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="ເພີ່ມຫມາຍເຫດທົ່ວໄປ..."
          rows={3}
        />
      </div>

      {/* Status Summary */}
      <div
        className="rounded-xl border bg-card p-4"
        data-tourid="record-form-status-summary"
      >
        <div className="flex items-center justify-between">
          <span className="font-semibold">ສະຖານະ:</span>
          <span
            className={`font-bold ${
              overallStatus === "pass"
                ? "text-green-600"
                : overallStatus === "fail"
                  ? "text-red-600"
                  : "text-yellow-600"
            }`}
          >
            {overallStatus === "pass"
              ? "ຜ່ານ"
              : overallStatus === "fail"
                ? "ບໍ່ຜ່ານ"
                : "ກຳລັງກວດສອບ"}
          </span>
        </div>
        <div className="mt-2 text-muted-foreground text-sm">
          ກວດສອບແລ້ວ: {items.filter((i) => i.status !== null).length} /{" "}
          {items.length}
        </div>
      </div>

      {/* Submit Button */}
      <div className="sticky bottom-0 bg-background pt-4">
        <Button
          onClick={handleSubmit}
          disabled={!allItemsChecked || submitting}
          size="lg"
          className="w-full py-6 text-base"
          data-tourid="record-form-submit"
        >
          {submitting ? "ກຳລັງບັນທຶກ..." : isEdit ? "ບັນທຶກການແກ້ໄຂ" : "ບັນທຶກຜົນກວດ"}
        </Button>
        {!allItemsChecked && (
          <p className="mt-2 text-center text-destructive text-sm">
            ກະລຸນາກວດສອບທຸກລາຍການທີ່ຈໍາເປັນ
          </p>
        )}
      </div>
    </div>
  );
}
